// KPI-KÁRTYA — érték-központú (nem link) összegző csempe: cím + ikon, nagy érték + opcionális badge, alatta
// alfelirat, láb-badge-sor és info-tooltipes leírás. Kiemelt (`highlight`) variáns a hangsúlyos számhoz.
//
// MIÉRT KÖZÖS RÉTEG: a bevétel-kimutatás KPI-sora (MRR / PRR / NRR / ARR / AR) mellett a Pénzügy Összegzés
// áttekintése is ezt használja (haszonkulcs · kintlévőség · ügyfél-koncentráció · következő hónap) — a lap két
// kártya-sora így egy nyelvet beszél.

import { InformationCircleIcon } from '@heroicons/react/20/solid';
import { type ComponentType, type ReactNode, type SVGProps } from 'react';

import { Text } from '@/components/ui/text';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

export function StatCard({
  title,
  value,
  description,
  info,
  badge,
  footer,
  subtitle,
  action,
  icon: Icon,
  highlight = false,
}: {
  title: string;
  /** A már formázott (Decimal-string → locale) összeg. */
  value: string;
  /** Rövid magyarázó a kártya alján (a legacy `text-xs` supporting szövege). */
  description?: string;
  /** Info-tooltip tartalma (a leírás mellé kerülő „i"). */
  info?: string;
  /** Jobb-felső / érték melletti badge (pl. változás-%). */
  badge?: ReactNode;
  /** Láb-badge-sor (pl. PRR frekvencia-bontás / státusz-bontás). */
  footer?: ReactNode;
  /** Az érték alatti dinamikus alfelirat (pl. „Projected MRR"). */
  subtitle?: string;
  /** Jobb-felső akció (pl. a részlet-modált nyitó „szem" ikon — a legacy 1:1). */
  action?: ReactNode;
  icon?: ComponentType<SVGProps<SVGSVGElement>>;
  highlight?: boolean;
}): JSX.Element {
  return (
    <div
      className={cn(
        'card-glass text-card-foreground flex flex-col gap-2 rounded-xl p-4',
        highlight && 'border-brand/40 from-brand/10 to-brand/5 bg-gradient-to-br',
      )}
    >
      <div className="flex items-center justify-between gap-2">
        <Text variant="secondary" as="span" className="min-w-0 truncate font-medium">
          {title}
        </Text>
        <div className="flex shrink-0 items-center gap-1.5">
          {action}
          {Icon && (
            <span
              className={cn(
                'flex size-8 shrink-0 items-center justify-center rounded-lg',
                highlight ? 'bg-brand/15 text-brand' : 'bg-muted text-muted-foreground',
              )}
            >
              <Icon className="size-4" />
            </span>
          )}
        </div>
      </div>

      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
        {/* A 9 jegyű összeg (pl. „824 638 084 HUF") a 2-hasábos MOBIL rácsban 4px-el túlcsordult (mérve
            375px-en) — a legkisebb sávon egy fokkal kisebb szedés, `sm`-től a teljes méret. */}
        <span className="text-foreground text-xl font-semibold tabular-nums tracking-tight sm:text-2xl">
          {value}
        </span>
        {badge}
      </div>

      {subtitle && (
        <Text variant="secondary" as="span" className="text-xs">
          {subtitle}
        </Text>
      )}

      {footer && <div className="flex flex-wrap items-center gap-1.5">{footer}</div>}

      {(description ?? info) && (
        <div className="text-muted-foreground mt-auto flex items-center gap-1 pt-1 text-xs">
          {description && <span className="min-w-0 truncate">{description}</span>}
          {info && (
            <TooltipProvider delayDuration={150}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    aria-label={info}
                    className="text-muted-foreground hover:text-foreground focus-visible:ring-ring shrink-0 rounded-full focus:outline-none focus-visible:ring-2"
                  >
                    <InformationCircleIcon className="size-3.5" />
                  </button>
                </TooltipTrigger>
                <TooltipContent className="max-w-56">{info}</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
      )}
    </div>
  );
}
